import React from 'react';

const FETCH_LABELS = {
  timeEntries: 'time entries',
  invoices: 'invoices',
  writeOffs: 'write-offs',
};

/**
 * Rendered whenever `useClientRollup(...).partial` is true — at least one
 * fetch behind the figures on this page failed.
 *
 * This is not cosmetic. A failed time-entries/invoices/write-offs fetch does
 * not blank the page — the roll-up still totals whatever DID load — but that
 * means a confident-looking WIP/A/R number can be silently LOWER than the
 * client's real position (missing time understates WIP; missing invoices
 * understate what's actually been billed). On a page an accountant uses to
 * decide what to bill, an understated number that looks complete is worse
 * than an obvious error. So: say so, plainly, every time it's true.
 *
 * Kept deliberately dumb (two props, no fetching, no knowledge of "group" vs
 * "client") so Tasks 10/11 can reuse it as-is on other pages.
 */
export default function IncompleteDataBanner({ failedClientIds, failedFetches }) {
  const clientIds = failedClientIds || [];
  const fetchEntries = Object.entries(failedFetches || {})
    .filter(([, ids]) => (ids || []).length > 0);

  if (clientIds.length === 0 && fetchEntries.length === 0) return null;

  return (
    <div
      role="alert"
      style={{
        background: '#fef2f2',
        border: '1px solid #fecaca',
        color: '#991b1b',
        borderRadius: 8,
        padding: '12px 16px',
        marginBottom: 20,
        fontSize: 14,
        lineHeight: 1.5,
      }}
    >
      <strong>Incomplete data — the figures below cannot be trusted.</strong>
      <div style={{ marginTop: 4 }}>
        Some data failed to load, so totals (WIP especially) may be
        understated rather than overstated.
        {clientIds.length > 0 && (
          <span>
            {' '}Could not load client{clientIds.length === 1 ? '' : 's'}: {clientIds.join(', ')}.
          </span>
        )}
        {fetchEntries.map(([key, ids]) => (
          <span key={key}>
            {' '}Could not load {FETCH_LABELS[key] || key} for client{ids.length === 1 ? '' : 's'} {ids.join(', ')}.
          </span>
        ))}
      </div>
    </div>
  );
}
